import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import _ from 'lodash'
import { useCallback, useEffect } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Separator } from '@/components/elements/separator'
import { Skeleton } from '@/components/elements/skeleton'
import { Textarea } from '@/components/elements/textarea'
import { Layout } from '@/components/Layout'
import { PDF_BLOCK_DEFAULTS } from '@/components/PdfBlockOptions'
import {
  addCourse,
  deleteCourse as deleteCourseRequest,
  getCourse,
  updateCourse,
} from '@/services/courses.service'
import { cn, toast } from '@/utils/helpers'
import { getQuizConfigError } from '@/utils/quiz'

import ContentBlock from './ContentBlock'

const statusStyles = {
  Draft: 'bg-primary-muted text-primary',
  Published: 'bg-primary text-white',
  Archived: 'bg-muted-foreground text-white',
}

const Course = () => {
  const navigate = useNavigate()
  const { courseId } = useParams()
  const isNew = courseId === 'new'

  // Form
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      isXverseCourse: true,
      // Must be an array — useFieldArray throws on null defaults, and in
      // isNew mode the form isn't reset from server data before render.
      contents: [],
    },
  })

  const courseName = useWatch({
    control: form.control,
    name: 'name',
  })
  const {
    fields: contentBlocks,
    append,
    move,
    remove,
    update,
  } = useFieldArray({
    control: form.control,
    name: 'contents',
  })

  /**
   * Resets the course form with the provided data.
   * @param {object} data
   */
  const resetCourse = useCallback(
    (data) => {
      form.reset({
        ...data,
        contents: _.map(data.contents, (c) => {
          let updatedQuiz = null

          if (c.quiz) {
            updatedQuiz = { ...c.quiz }
            // Default to full question count if not configured
            updatedQuiz.numQuestionsToPass =
              updatedQuiz.numQuestionsToPass || updatedQuiz.questions.length
            updatedQuiz.numQuestionsToDisplay =
              updatedQuiz.numQuestionsToDisplay || updatedQuiz.questions.length

            // Ensure answers are in the correct format
            updatedQuiz.questions = _.map(updatedQuiz.questions, (q) => ({
              ...q,
              answers: _.map(_.values(q.answers), (a) => ({ ...a, value: a.value ?? a.text })),
            }))
          }

          return {
            ...c,
            // Seed display label from the denormalized asset_name so the
            // picker shows the asset's name after load, not just its id.
            assetLabel: c.assetName ?? '',
            quiz: updatedQuiz,
          }
        }),
      })
    },
    [form],
  )

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: course, isPending: loadingCourse } = useQuery({
    queryKey: ['courses', courseId],
    queryFn: () => getCourse(courseId),
    placeholderData: keepPreviousData,
    enabled: !isNew,
  })
  const { isPending: loadingUpdate, mutate: mutateCourse } = useMutation({
    // Optional second arg names a target state so state transitions
    // (Publish / Archive / Re-publish) get an explicit success toast.
    // The quiz check lives here rather than in onSubmit so the Publish /
    // Archive / Re-publish buttons, which call mutateCourse directly, can't
    // route around it (RSCX-385).
    mutationFn: ({ payload }) => {
      const quizError = getQuizConfigError(payload.contents)
      if (quizError) throw new Error(quizError)
      return updateCourse(payload)
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data, { intent }) => {
      const msg = (() => {
        if (intent === 'Published') return `${data.name} published`
        if (intent === 'Archived') return `${data.name} archived`
        if (intent === 'Republished') return `${data.name} re-published`
        return `${data.name} updated successfully`
      })()
      handleSuccess(msg)
      resetCourse(data)

      // Refresh courses list
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })
  const { isPending: loadingCreate, mutate: createCourse } = useMutation({
    mutationFn: (data) => {
      const quizError = getQuizConfigError(data.contents)
      if (quizError) throw new Error(quizError)
      return addCourse(data)
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} created successfully`)
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      // Hop to the saved record so the user can keep editing (publish/archive
      // controls only make sense once an id exists). replace: true so back
      // doesn't bounce them to /new.
      navigate(`/admin/courses/course-modules/${data.id}`, { replace: true })
    },
  })
  const { isPending: loadingDelete, mutate: deleteCourse } = useMutation({
    mutationFn: () => deleteCourseRequest(courseId),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Course deleted successfully')

      // Refresh course list and redirect to courses list
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      navigate('/admin/courses/course-modules')
    },
  })

  useEffect(() => {
    if (course) {
      resetCourse(course)
    }
  }, [course, resetCourse])

  const orderContent = useCallback((dragIndex, hoverIndex) => move(dragIndex, hoverIndex), [move])
  const renderContent = useCallback(
    (content, index) => (
      <ContentBlock
        courseId={courseId}
        content={content}
        index={index}
        key={content.id}
        name={`contents.${index}`}
        orderContent={orderContent}
        // New course doesn't exist yet — treat as Draft so the editor is open.
        readOnly={!isNew && course?.state !== 'Draft'}
        remove={remove}
        update={update}
      />
    ),
    [course, courseId, isNew, orderContent, remove, update],
  )

  const transformData = (data) => ({
    ...data,
    isXverseCourse: true,
    contents: _.map(data.contents, (c, i) => {
      // assetLabel is display-only; BE doesn't know about it.
      const { assetLabel: _label, ...rest } = c
      const updatedContent = { ...rest, order: i }

      if (c.quiz) {
        updatedContent.quiz = {
          ...c.quiz,
          numQuestionsToPass: c.quiz.numQuestionsToPass || c.quiz.questions.length,
          numQuestionsToDisplay: c.quiz.numQuestionsToDisplay || c.quiz.questions.length,
          questions: _.map(c.quiz.questions, (q, j) => ({
            ...q,
            order: j,
            answers: _.map(q.answers, (a, k) => ({ ...a, displayOrder: k })),
          })),
        }
      }

      return updatedContent
    }),
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => {
    if (isNew) {
      createCourse(transformData(data))
    } else {
      mutateCourse({ payload: transformData(data) })
    }
  }

  const renderCourseStatusButton = () => {
    if (course.state === 'Archived')
      return (
        <ConfirmButton
          type="button"
          variant="outline"
          className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
          disabled={loadingDelete}
          onClick={() =>
            mutateCourse({
              payload: transformData({ ...form.getValues(), state: 'Published' }),
              intent: 'Republished',
            })
          }
          confirmContent={`Are you sure you want to save and Re-publish the course module: ${courseName}?`}
          confirmTitle="Republish Course Module"
        >
          Re-publish Course Module
        </ConfirmButton>
      )

    if (course.state === 'Draft') {
      return (
        <ConfirmButton
          type="button"
          className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
          disabled={loadingDelete}
          onClick={() =>
            mutateCourse({
              payload: transformData({ ...form.getValues(), state: 'Published' }),
              intent: 'Published',
            })
          }
          confirmContent={`Are you sure you want to save and publish the course module: ${courseName}?`}
          confirmTitle="Publish Course Module"
        >
          Publish Course Module
        </ConfirmButton>
      )
    }

    return (
      <ConfirmButton
        type="button"
        variant="outline"
        className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
        disabled={loadingDelete}
        onClick={() =>
          mutateCourse({
            payload: transformData({ ...form.getValues(), state: 'Archived' }),
            intent: 'Archived',
          })
        }
        confirmContent={`Are you sure you want to save and archive the course module: ${courseName}?`}
        confirmTitle="Archive Course Module"
      >
        Archive Course Module
      </ConfirmButton>
    )
  }

  if (!isNew && loadingCourse) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { title: 'Course Modules', pathname: '/admin/courses/course-modules' },
          'Course Module',
        ]}
      >
        <div className="flex h-full w-full flex-col gap-4 p-4 sm:p-6 lg:p-8">
          <Skeleton className="h-6 w-80 rounded-sm" />

          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
          <Separator />
          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
          <Separator />
          <div className="flex w-full flex-row gap-4">
            <div className="sm:w-1/4">
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>

            <div className="flex w-full flex-col gap-4">
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
              <Skeleton className="h-6 w-full rounded-sm" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout
      breadcrumbs={[
        'Admin',
        { title: 'Course Modules', pathname: '/admin/courses/course-modules' },
        // Must be a defined string — Layout's pageTitle reads `.title` on
        // non-string items without guarding (would crash on undefined).
        isNew ? 'New' : (course?.name ?? ''),
      ]}
    >
      <Layout.Heading title={isNew ? 'New Course Module' : course?.name} />

      {!isNew && (
        <div className="mt-2 flex flex-row items-center gap-2">
          <span className={cn('rounded-full px-2 py-1 text-sm', statusStyles[course?.state])}>
            {course?.state}
          </span>

          {course?.modifiedAt && (
            <span className="text-muted-foreground text-xs">
              Last Updated: {dayjs(course.modifiedAt).format('MM/DD/YY hh:mm A')}
            </span>
          )}
        </div>
      )}

      <div className="mt-1 flex shrink-0 flex-row items-start justify-start gap-4 sm:justify-end">
        {isNew && (
          <Button
            type="button"
            variant="outline"
            className="w-fit"
            onClick={() => navigate('/admin/courses/course-modules')}
            disabled={loadingCreate}
          >
            Cancel
          </Button>
        )}

        {!isNew && course?.state === 'Draft' && (
          <ConfirmButton
            type="button"
            variant="destructive"
            className={cn('w-fit', loadingUpdate && 'cursor-not-allowed')}
            disabled={loadingDelete}
            onClick={() => deleteCourse()}
            confirmContent={`Are you sure you want to delete the course: ${courseName}?`}
            confirmTitle="Delete Course Module"
          >
            Delete Course Module
          </ConfirmButton>
        )}

        {(isNew || course?.state !== 'Archived') && (
          <Button
            type="submit"
            variant="secondary"
            className={cn('w-fit', (loadingUpdate || loadingCreate) && 'cursor-not-allowed')}
            disabled={loadingUpdate || loadingCreate}
            onClick={form.handleSubmit(onSubmit)}
          >
            {(() => {
              if (isNew) return loadingCreate ? 'Creating...' : 'Create Course Module'
              return loadingUpdate ? 'Saving...' : 'Save Changes'
            })()}
          </Button>
        )}

        {!isNew && renderCourseStatusButton()}
      </div>

      <div className="h-full w-full flex-col overflow-y-auto px-4 pb-12 sm:px-6 lg:px-8">
        <Form {...form}>
          <form className="flex flex-col gap-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <span className="text-muted-foreground font-semibold sm:w-1/4">General</span>

              <div className="flex w-full flex-col gap-8">
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input id="name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: 'This field is required' }}
                />
                <FormField
                  control={form.control}
                  disabled={loadingUpdate}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea id="description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* TODO: May need in the future when accounts can add courses */}
                {/* <FormField
                    control={form.control}
                    disabled={loadingUpdate}
                    name="isXverseCourse"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2">
                        <FormControl>
                          <Checkbox
                            id="isXverseCourse"
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <FormLabel>Xverse Course</FormLabel>
                      </FormItem>
                    )}
                  /> */}
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
              <div className="sm:w-1/4">
                <span className="text-muted-foreground font-semibold">Content Blocks</span>
                <p className="text-muted-foreground text-xs">
                  Click and drag to reorder content blocks.
                </p>
              </div>

              <div className="flex w-full flex-col gap-8">
                {contentBlocks.length > 0 ? (
                  contentBlocks.map((content, index) => renderContent(content, index))
                ) : (
                  <span className="text-muted-foreground self-center text-sm">
                    No content blocks added yet.
                  </span>
                )}

                {(isNew || course?.state === 'Draft') && (
                  <Button
                    type="button"
                    className="w-fit self-start"
                    size="sm"
                    onClick={() =>
                      append({
                        type: 'Quiz Only',
                        asset: null,
                        assetLabel: '',
                        order: contentBlocks.length,
                        course: isNew ? null : courseId,
                        quiz: null,
                        name: '',
                        ...PDF_BLOCK_DEFAULTS,
                      })
                    }
                  >
                    Add Content Block
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </div>
    </Layout>
  )
}

export default Course
